import { observer } from 'mobx-react';
import { FC, useEffect, useMemo } from 'react';
import { Prompt } from 'react-router';
import { useParams } from 'react-router-dom';
import CashbackActionPanel from '../../components/CashbackActionPanel/CashbackActionPanel';
import CashbackAvailability from '../../components/CashbackAvailability/CashbackAvailability';
import CashbackBalance from '../../components/CashbackBalance/CashbackBalance';
import CashbackBranches from '../../components/CashbackBranches/CashbackBranches';
import CashbackCount from '../../components/CashbackCount/CashbackCount';
import CashbackCredit from '../../components/CashbackCredit/CashbackCredit';
import CashbackDescription from '../../components/CashbackDescription/CashbackDescription';
import CashbackName from '../../components/CashbackName/CashbackName';
import CashbackValidity from '../../components/CashbackValidity/CashbackValidity';
import { cashbackStore } from '../../store/cashbackStore';
import { SCREENS } from 'src/constants/screens';
import { scrollToErrorElement } from 'src/utils/helpers';
import { getPromptProps } from 'src/view/components/CustomPrompt/helper';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { getBackStepPath } from '~/helpers';
import { localeStore } from '~/mobx/localesStore';
import { history } from '~/services/history';
import Headers from '~/view/containers/Headers/Headers';
import HeaderBack from '~/view/containers/Headers/components/HeaderBack/HeaderBack';
import './style.scss';
import CashbackOrderTypes from '~/view/screens/MarketingScreen/screens/Cashback/components/CashbackOrderTypes/CashbackOrderTypes';
import Text, { TEXT } from '~/view/components/Text/Text';
import './style.scss';
import { confirmDelete } from '~/utils/modalConfirmations';

const CashbackEdit: FC = observer((): JSX.Element => {
  const { id: cashbackId } = useParams<{ id?: string }>();
  const backStepPath = useMemo((): string => getBackStepPath(SCREENS.CASHBACK()), []);

  useEffect(() => {
    if (cashbackId) {
      const fetchCashbackInfo = async () => {
        await cashbackStore.getCashback(Number(cashbackId));
        await cashbackStore.getActiveCashbacksCount();
      };

      fetchCashbackInfo();
    } else {
      cashbackStore.updateCashbackStateBeforeCreation();
    }

    return () => cashbackStore.reset();
  }, []);

  const handleClose = () => {
    history.push(SCREENS.CASHBACK());
  };

  const handleSave = async () => {
    cashbackStore.validate();

    if (cashbackStore.isValidationFailed) {
      scrollToErrorElement();
      return;
    }

    let result: boolean | undefined;

    if (cashbackId) {
      result = await cashbackStore.editCashback(Number(cashbackId), false, handleClose);
    } else {
      result = await cashbackStore.createCashback(false, handleClose);
    }

    if (result) handleClose();
  };

  const handleDelete = async () => {
    confirmDelete({
      title: localeStore.t('are_you_sure'),
      message: localeStore.t('txt_cart_item_delete_confirm'),
      onConfirm: async () => {
        const response = await cashbackStore.deleteCashback(Number(cashbackId));
        if (response) handleClose();
      },
    });
  };

  return (
    <div className="d-flex flex-1 flex-column full-width">
      <Headers>
        <HeaderBack title={cashbackId ? localeStore.t('edit_cashback') : localeStore.t('create_cashback')} path={backStepPath} />
      </Headers>

      <ScrollableBlock showScrollbar className="d-flex flex-1 mr-1" scrollClassName="cashback-edit__content">
        <CashbackAvailability />
        <div className="d-flex full-width">
          <div className="d-flex flex-column w-50 mr-3">
            <CashbackName />
            <CashbackValidity />
          </div>
          <CashbackDescription />
        </div>
        <CashbackCount />
        <CashbackCredit />
        <CashbackBalance />

        <div className="d-flex flex-column">
          <Text text={localeStore.t('cashback_conditions_block_hdr')} type={TEXT.SECTION_HEADER} className="mt-6" />
          <CashbackBranches />
          {/* TODO uncomment when cashback will be released TK-1160 */}
          <CashbackOrderTypes />
        </div>

        <CashbackActionPanel onSave={handleSave} onDelete={handleDelete} showDelete={cashbackId !== undefined} />
      </ScrollableBlock>

      <LoadingOverlay visible={cashbackStore.loading} refreshing />
      <Prompt when={cashbackStore.isEdit} message={getPromptProps()} />
    </div>
  );
});

export default CashbackEdit;
